import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, Observable } from 'rxjs';
import { IChatConversation, IChatConversationScope, IPromptAuditEntry } from 'src/app/model/chat-conversation';
import { IAnthropic } from 'src/app/model/configuration/anthropic';
import { IConfiguration } from 'src/app/model/configuration/configuration';
import { ICopilotPrompting } from 'src/app/model/configuration/copilot-prompting';
import { ICsv } from 'src/app/model/configuration/csv';
import { IEmail } from 'src/app/model/configuration/email';
import { IGemini } from 'src/app/model/configuration/gemini';
import { IModelCatalogs, IProviderModelCatalog, IProviderModelCatalogDiff, ModelCatalogProvider } from 'src/app/model/configuration/model-catalog';
import { INotification } from 'src/app/model/configuration/notification';
import { IOllama } from 'src/app/model/configuration/ollama';
import { IOpenai } from 'src/app/model/configuration/openai';
import { DefaultConfig } from 'src/assets/default.config';
import { Cert, ConfigurationService } from '../configuration.service';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationServiceImpl implements ConfigurationService {
  constructor(
    private http: HttpClient
  ) {}

  public async getConfiguration(): Promise<IConfiguration> {
    const uri = DefaultConfig.baseConfigurationManagement.configuration;

    const configuration = (await lastValueFrom(this.http.get<IConfiguration>(
      uri
    )))!;

    return configuration;
  }

  public updateEmailConfiguration(email: IEmail): Observable<IConfiguration> {
    const uri = DefaultConfig.baseConfigurationManagement.email;

    return this.http.put<IConfiguration>(uri, email);
  }

  public updateCsvConfiguration(csv: ICsv): Observable<IConfiguration> {
    const uri = DefaultConfig.baseConfigurationManagement.csv;

    return this.http.put<IConfiguration>(uri, csv);
  }

  public updateNotificationConfiguration(notification: INotification): Observable<IConfiguration> {
    const uri = DefaultConfig.baseConfigurationManagement.notification;

    return this.http.put<IConfiguration>(uri, notification);
  }

  public updateOpcConfiguration(opc: IConfiguration['opc'] & { password: string | undefined }): Observable<IConfiguration> {
    const uri = DefaultConfig.baseConfigurationManagement.opc;

    return this.http.put<IConfiguration>(uri, opc);
  }
  public getOpcTrustedCertificates(): Observable<Cert[]> {
    const uri = DefaultConfig.opc.uri.trustedCertificates;

    return this.http.get<Cert[]>(uri);
  }

  public getOpcQuarantinedCertificates(): Observable<Cert[]> {
    const uri = DefaultConfig.opc.uri.quarantinedCertificates;

    return this.http.get<Cert[]>(uri);
  }
  public addOpcTrustedCertificates(cert: Cert): Observable<void> {
    const uri = DefaultConfig.opc.uri.trustCertificate;

    return this.http.post<void>(uri, cert);
  }

  public  revokeOpcTrustedCertificates(cert: Cert): Observable<void> {
    const uri = DefaultConfig.opc.uri.revokeCertificate;

    return this.http.post<void>(uri, cert);
  }

  public async getCopilotPromptingSettings(): Promise<ICopilotPrompting> {
    const uri = DefaultConfig.baseConfigurationManagement.copilotPrompting;

    return (await lastValueFrom(this.http.get<ICopilotPrompting>(uri)))!;
  }

  public updateCopilotPromptingConfiguration(copilotPrompting: ICopilotPrompting): Observable<IConfiguration> {
    const uri = DefaultConfig.baseConfigurationManagement.copilotPrompting;

    return this.http.put<IConfiguration>(uri, copilotPrompting);
  }

  public updateOpenaiConfiguration(openai: IOpenai): Observable<IConfiguration> {
    const uri = DefaultConfig.baseConfigurationManagement.openai;

    return this.http.put<IConfiguration>(uri, openai);
  }

  public async getOpenaiSettings(): Promise<IOpenai> {
    const uri = DefaultConfig.baseConfigurationManagement.openai;

    return (await lastValueFrom(this.http.get<IOpenai>(uri)))!;
  }

  public async getAnthropicSettings(): Promise<IAnthropic> {
    const uri = DefaultConfig.baseConfigurationManagement.anthropic;

    return (await lastValueFrom(this.http.get<IAnthropic>(uri)))!;
  }

  public updateAnthropicConfiguration(anthropic: IAnthropic): Observable<IConfiguration> {
    const uri = DefaultConfig.baseConfigurationManagement.anthropic;

    return this.http.put<IConfiguration>(uri, anthropic);
  }

  public async getGeminiSettings(): Promise<IGemini> {
    const uri = DefaultConfig.baseConfigurationManagement.gemini;

    return (await lastValueFrom(this.http.get<IGemini>(uri)))!;
  }

  public updateGeminiConfiguration(gemini: IGemini): Observable<IConfiguration> {
    const uri = DefaultConfig.baseConfigurationManagement.gemini;

    return this.http.put<IConfiguration>(uri, gemini);
  }

  public async getOllamaSettings(): Promise<IOllama> {
    const uri = DefaultConfig.baseConfigurationManagement.ollama;

    return (await lastValueFrom(this.http.get<IOllama>(uri)))!;
  }

  public updateOllamaConfiguration(ollama: IOllama): Observable<IConfiguration> {
    const uri = DefaultConfig.baseConfigurationManagement.ollama;

    return this.http.put<IConfiguration>(uri, ollama);
  }

  public async getOllamaVisionModelCatalog(): Promise<IProviderModelCatalog> {
    const uri = DefaultConfig.baseConfigurationManagement.ollamaVisionModels;

    return (await lastValueFrom(this.http.get<IProviderModelCatalog>(uri)))!;
  }

  public async resolveOllamaVisionModelCatalog(ollama: IOllama): Promise<IProviderModelCatalog> {
    const uri = DefaultConfig.baseConfigurationManagement.resolveOllamaVisionModels;

    return (await lastValueFrom(this.http.post<IProviderModelCatalog>(uri, ollama)))!;
  }

  public async getModelCatalogs(): Promise<IModelCatalogs> {
    const uri = DefaultConfig.baseConfigurationManagement.modelCatalogs;

    return (await lastValueFrom(this.http.get<IModelCatalogs>(uri)))!;
  }

  public async getModelCatalog(provider: ModelCatalogProvider): Promise<IProviderModelCatalog> {
    const uri = DefaultConfig.baseConfigurationManagement.modelCatalog(provider);

    return (await lastValueFrom(this.http.get<IProviderModelCatalog>(uri)))!;
  }

  public async getModelCatalogDiff(provider: ModelCatalogProvider): Promise<IProviderModelCatalogDiff> {
    const uri = DefaultConfig.baseConfigurationManagement.modelCatalogDiff(provider);

    return (await lastValueFrom(this.http.get<IProviderModelCatalogDiff>(uri)))!;
  }

  public async refreshModelCatalog(provider: ModelCatalogProvider): Promise<IProviderModelCatalogDiff> {
    const uri = DefaultConfig.baseConfigurationManagement.modelCatalogRefresh(provider);

    return (await lastValueFrom(this.http.post<IProviderModelCatalogDiff>(uri, {})))!;
  }

  public async createChatkitSession(): Promise<{ client_secret: string; promptAudit?: IPromptAuditEntry }> {
    const uri = DefaultConfig.baseConfigurationManagement.chatkitSession;

    return (await lastValueFrom(this.http.post<{ client_secret: string; promptAudit?: IPromptAuditEntry }>(uri, {})))!;
  }

  public async syncChatkitThread(
    threadId: string,
    promptAuditTrail: IPromptAuditEntry[] = [],
    conversationScope: IChatConversationScope | null = null
  ): Promise<IChatConversation> {
    const uri = DefaultConfig.baseConfigurationManagement.chatkitThreadSync;

    return (await lastValueFrom(this.http.post<IChatConversation>(uri, {
      threadId,
      promptAuditTrail,
      ...(conversationScope ?? {})
    })))!;
  }
}
